import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  GraduationCap,
  Calculator,
  Atom,
  Microscope,
  Scale,
  TrendingUp,
  BookOpen,
  Palette,
  ArrowRight,
} from 'lucide-react';
import TiltCard from './effects/TiltCard';
import { subjectColors } from '../utils/noteLabels';

type CategoryItem = {
  subjectKey: string;
  label: string;
  desc: string;
  icon: React.ElementType;
  span?: 1 | 2;
};

const categories: CategoryItem[] = [
  {
    subjectKey: 'matematik',
    label: 'Mühendislik & Matematik',
    desc: 'Calculus, lineer cebir, olasılık ve mühendislik ders notları.',
    icon: Calculator,
    span: 2,
  },
  {
    subjectKey: 'biyoloji',
    label: 'Tıp & Biyoloji',
    desc: 'Anatomi, biyokimya ve sağlık bilimleri.',
    icon: Microscope,
  },
  {
    subjectKey: 'tarih',
    label: 'Hukuk & Sosyal',
    desc: 'Anayasa, medeni hukuk ve sosyal bilimler.',
    icon: Scale,
  },
  {
    subjectKey: 'fizik',
    label: 'Fen Bilimleri',
    desc: 'Fizik, kimya ve temel bilimler.',
    icon: Atom,
  },
  {
    subjectKey: 'edebiyat',
    label: 'Edebiyat & Sanat',
    desc: 'Türk dili, edebiyat ve beşeri bilimler.',
    icon: BookOpen,
  },
  {
    subjectKey: 'felsefe',
    label: 'İktisat & İşletme',
    desc: 'Mikro-makro, muhasebe ve yönetim dersleri.',
    icon: TrendingUp,
    span: 2,
  },
  {
    subjectKey: 'cografya',
    label: 'Mimarlık & Tasarım',
    desc: 'Şehir planlama, grafik ve tasarım notları.',
    icon: Palette,
  },
];

const SubjectCategories: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section id="categories" className="page-section">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '56px' }}
        >
          <div className="section-badge" style={{ margin: '0 auto 16px' }}>
            <GraduationCap size={14} />
            Üniversite bölümleri
          </div>
          <h2
            style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
              fontWeight: 800,
              margin: '0 0 12px',
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            <span className="gradient-text-white">Ders alanına göre keşfet</span>
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '15px', maxWidth: '560px', margin: '0 auto', lineHeight: 1.65 }}>
            Onaylı not arşivinde bölümüne uygun içerikleri filtrele — sahte sayılar yok, gerçek paylaşımlar.
          </p>
        </motion.div>

        <div className="bento-grid">
          {categories.map((cat, index) => {
            const Icon = cat.icon;
            const color = subjectColors[cat.subjectKey] || 'var(--accent-primary)';
            return (
              <motion.div
                key={cat.subjectKey}
                className={cat.span === 2 ? 'bento-span-2' : undefined}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
              >
                <TiltCard
                  glowColor={`${color}22`}
                  onClick={() => navigate(`/notlar?subject=${cat.subjectKey}`)}
                  className="glow-card bento-card"
                  style={{
                    padding: '28px 26px',
                    borderRadius: 'var(--radius-card, 22px)',
                    cursor: 'pointer',
                    height: '100%',
                    minHeight: '160px',
                    border: `1px solid ${color}25`,
                  }}
                >
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '16px',
                      background: `${color}15`,
                      border: `1px solid ${color}35`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <Icon size={26} color={color} />
                  </div>
                  <h3
                    style={{
                      margin: '0 0 8px',
                      fontSize: '1.15rem',
                      fontWeight: 700,
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    {cat.label}
                  </h3>
                  <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: '13px', lineHeight: 1.55 }}>{cat.desc}</p>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      color,
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    Notları gör
                    <ArrowRight size={14} />
                  </span>
                </TiltCard>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SubjectCategories;
